import { PreviewMessageType, type WebContainer } from '@webcontainer/api';
import { atom } from 'nanostores';
import { cleanStackTrace } from '~/lib/utils/stackTraceUtils';
import { chatStore } from '~/lib/stores/chat';

export interface PreviewInfo {
  port: number;
  ready: boolean;
  baseUrl: string;
}

export interface PreviewError {
  type: PreviewMessageType | 'TERMINAL_ERROR';
  message: string;
  stack?: string;
}

export class PreviewsStore {
  #availablePreviews = new Map<number, PreviewInfo>();
  #webcontainer: Promise<WebContainer>;

  previews = atom<PreviewInfo[]>([]);
  previewError = atom<PreviewError[]>([]);

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    this.#init();
  }

  async #init() {
    const webcontainer = await this.#webcontainer;

    webcontainer.on('port', (port, type, url) => {
      console.log('webcontainer port', port, type, url);

      let previewInfo = this.#availablePreviews.get(port);

      if (type === 'close' && previewInfo) {
        this.#availablePreviews.delete(port);
        this.previews.set(this.previews.get().filter((preview) => preview.port !== port));

        return;
      }

      const previews = this.previews.get();

      if (!previewInfo) {
        previewInfo = { port, ready: type === 'open', baseUrl: url };
        this.#availablePreviews.set(port, previewInfo);
        previews.push(previewInfo);
      }

      previewInfo.ready = type === 'open';
      previewInfo.baseUrl = url;

      this.previews.set([...previews]);
    });

    webcontainer.on('server-ready', (port, url) => {
      console.log('webcontainer server-ready', port, url);
    });

    webcontainer.on('error', (error) => {
      console.error('webcontainer error', error);
    });

    webcontainer.on('preview-message', (message) => {
      const previewError = this.previewError.get();
      const isAIStreaming = chatStore.get().isStreaming;

      console.log('isStreaming', isAIStreaming);

      if (isAIStreaming) {
        console.info('PreviewsStore: AI is streaming, ignoring preview message:', message);
        return; // Don't process or store the error if AI is streaming
      }

      if (message.type === 'PREVIEW_UNCAUGHT_EXCEPTION') {
        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: message.message,
            stack: cleanStackTrace(message.stack),
          },
        ]);
      }

      if (message.type === 'PREVIEW_UNHANDLED_REJECTION') {
        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: message.message,
            stack: cleanStackTrace(message.stack),
          },
        ]);
      }

      if (message.type === 'PREVIEW_CONSOLE_ERROR') {
        const argsAsString = message.args
          .map((arg) => {
            try {
              return typeof arg === 'string' ? arg : JSON.stringify(arg, null, 2);
            } catch {
              return String(arg);
            }
          })
          .join('\n');

        console.log('argsAsString', argsAsString);

        // Skip WebSocket connection errors to webcontainer API
        const webSocketErrorPattern =
          /WebSocket connection to '[^']*\.local-credentialless\.webcontainer-api\.io\/' failed: Error during WebSocket handshake: Empty response/;

        if (webSocketErrorPattern.test(argsAsString)) {
          console.log('Skipping WebSocket connection error to webcontainer API');
          return;
        }

        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: 'Log Details',
            stack: cleanStackTrace(message.stack || '') + '\n\nConsole Args:\n' + argsAsString,
          },
        ]);
      }
    });

    // Add listener for terminal errors
    document.addEventListener('dualite-terminal-error', (event: Event) => {
      try {
        const terminalError = (event as CustomEvent).detail;
        const isAIStreaming = chatStore.get().isStreaming;

        console.log('[PreviewsStore] Received terminal error event:', {
          error: terminalError,
          command: terminalError.command,
          message: terminalError.message,
          stack: terminalError.stack,
        });

        // Skip if AI is streaming
        if (isAIStreaming) {
          console.info('[PreviewsStore] AI is streaming, ignoring terminal error:', terminalError);
          return;
        }

        // Filter out certain benign warnings
        if (this.isBenignError(terminalError.message)) {
          console.log('[PreviewsStore] Skipping benign error:', {
            message: terminalError.message,
            patterns: 'deprecation, npm notice, optional/peer deps',
          });
          return;
        }

        // Add to error state
        const currentErrors = this.previewError.get();
        const newError = {
          type: 'TERMINAL_ERROR' as const,
          message: `[Terminal] ${terminalError.message}`,
          stack: `Command: ${terminalError.command}\n\nOutput:\n${terminalError.stack}`,
        };

        console.log('[PreviewsStore] Adding new error to state:', {
          currentErrorCount: currentErrors.length,
          newError,
        });

        this.previewError.set([...currentErrors, newError]);
        console.log('[PreviewsStore] Updated error state, new total errors:', this.previewError.get().length);
      } catch (error) {
        console.error('[PreviewsStore] Failed to handle terminal error event:', error);
      }
    });

    // Listen for error resolutions
    document.addEventListener('dualite-terminal-error-resolved', () => {
      console.log('[PreviewsStore] Received error resolution event');
      console.log('[PreviewsStore] Processed errors cache cleared');
    });
  }

  // Helper to filter out known benign errors/warnings
  isBenignError(message: string): boolean {
    try {
      console.log('[PreviewsStore] Checking if error is benign:', message);

      const benignPatterns = [
        /warning.*deprecated/i,
        /npm notice/i,
        /optional dependency.*failed/i,
        /peer dependency/i,
        /warning .* latest version is/i,
      ];

      const isBenign = benignPatterns.some((pattern) => pattern.test(message));
      console.log('[PreviewsStore] Benign error check result:', {
        message,
        isBenign,
        matchedPattern: isBenign ? benignPatterns.find((p) => p.test(message))?.toString() : 'none',
      });

      return isBenign;
    } catch (error) {
      console.error('[PreviewsStore] Error in benign error check:', error);
      return false;
    }
  }

  clearError() {
    this.previewError.set([]);
  }
}
